"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Hero background media: the film, over the section's ink ground.
 *
 * There is no still behind the film. The photograph that used to sit here was
 * a holdover from before the film existed, and it showed a version of the team
 * shot the clinic no longer wants published. The hero section carries `bg-ink`
 * and the scrim, so the headline and pills stay legible whenever the film is
 * absent.
 *
 * The film loads in an effect and fades in once it actually plays. Under
 * prefers-reduced-motion it is never requested at all, and those visitors get
 * the ink ground: a deliberate trade, since honouring the motion preference
 * matters more than the moving backdrop. Same outcome if the file fails to
 * load or has not arrived yet.
 *
 * LCP is now the headline text rather than a multi-megabyte photograph, which
 * is faster, not slower.
 */
export function HeroMedia({ videoSrc }: { videoSrc: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const onCanPlay = () => {
      v.play()
        .then(() => setVideoReady(true))
        .catch(() => setVideoReady(false));
    };
    const onError = () => setVideoReady(false);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("error", onError);
    v.src = videoSrc;
    v.load();
    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("error", onError);
    };
  }, [videoSrc]);

  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      className={`absolute inset-0 object-cover object-center transition-opacity duration-[900ms] ease-out ${
        videoReady ? "opacity-100" : "opacity-0"
      }`}
      // Inline size: the unlayered globals.css base rule
      // (video { height: auto }) outranks layered Tailwind utilities,
      // which left the film at its intrinsic 16:9 height and an ink gap
      // below. Inline styles outrank both, so the film always covers
      // the full hero.
      style={{ height: "100%", width: "100%" }}
    />
  );
}
